export const ACCESS_WRITE_WAIT_MS = 10000;

const messages = {
  ACCESS_IDENTITY_CHANGED: 'The signed-in user changed. Review the page before submitting this change again.',
  ACTION_UNAVAILABLE: 'Your current permissions no longer allow this change.',
  ACCESS_CHECK_FAILED: 'Could not verify your current session. Check your access before submitting this change again.',
  ACCESS_REFRESH_TIMEOUT: 'The access check took too long. This change was not sent. Check your access and try again.',
  ACCESS_WRITE_CANCELLED: 'This change was cancelled before it was sent.',
};
type WriteAccessError = keyof typeof messages;
export const writeAccessError = (code: WriteAccessError) => ({
  status: 'CUSTOM_ERROR' as const, error: code, data: { message: messages[code] },
});

export const writeAccessMessage = (error: unknown): string | undefined => {
  const failure = error as { status?: unknown; error?: unknown } | undefined;
  if (failure?.status !== 'CUSTOM_ERROR' || typeof failure.error !== 'string' || failure.error === 'ACCESS_WRITE_CANCELLED') return;
  if (Object.prototype.hasOwnProperty.call(messages, failure.error)) return messages[failure.error as WriteAccessError];
};

// Each write waits on the shared refresh without owning or cancelling it.
// Completing/cancelling/timing out a waiter always releases its timer/listener.
export const waitForProjection = (
  request: unknown, signal: AbortSignal, remainingMs: number,
): Promise<WriteAccessError | undefined> =>
  new Promise(resolve => {
    if (remainingMs <= 0) { resolve('ACCESS_REFRESH_TIMEOUT'); return; }
    let finished = false;
    const finish = (error?: WriteAccessError) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      resolve(error);
    };
    const onAbort = () => finish('ACCESS_WRITE_CANCELLED');
    const timer = setTimeout(() => finish('ACCESS_REFRESH_TIMEOUT'), Math.max(0, remainingMs));
    signal.addEventListener('abort', onAbort);
    if (signal.aborted) onAbort();
    Promise.resolve(request).then(() => finish(), () => finish('ACCESS_CHECK_FAILED'));
  });
